import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { errText } from '../api/client';
import { authBase, config } from '../api/config';
import { Ic } from '../st8n-tickets/kit.jsx';

export const POINTS: [keyof typeof Ic, string, string][] = [
  ['Board', 'Boards your sessions work', 'Write the ticket once; a free st8n session picks it up, does it and reports back with the commit.'],
  ['Check', 'Done means done', 'Each ticket says what done means, and a session reports back with the evidence: a summary, the commit, the pull request.'],
  ['Share', 'Shared with your people', 'Owner, can work, can view. People join when they accept, and their sessions take the tickets of a shared board only if they switch that on.'],
];
/** The same points with st8n off: Deleg8 on its own (tickets, clients, the people who do them). */
export const STANDALONE_POINTS: [keyof typeof Ic, string, string][] = [
  ['Board', 'Boards for the work', 'Write the ticket once, say who should do it, and follow it from Open to Done.'],
  ['Check', 'Done means done', 'Each ticket says what done means, so whoever finishes it knows what to check.'],
  ['Share', 'Your team and your clients', 'Roles for your own people; clients send requests and see only the updates you publish to them.'],
];

/**
 * [LIC D1] What this site offers (GET /healthz signIn): its OpenID Connect sign-in (st8n unless `name` says another
 * provider) and, when on, email codes. Until it answers, and in the demo: st8n, as before. `setupNeeded`: a public install
 * with no DELEG8_OWNER_EMAILS and no owner yet, where nobody can make an account (the first-run banner).
 */
interface SignInOptions { oidc: boolean; name: string | null; email: boolean; local: boolean; mail: boolean; setupNeeded: boolean }
function useSignInOptions(): SignInOptions {
  const [o, setO] = useState<SignInOptions>({ oidc: config.mock && !isPortal(), name: null, email: false, local: true, mail: true, setupNeeded: false });
  useEffect(() => {
    if (config.mock) return;
    let live = true;
    fetch(authBase().replace(/\/auth$/, '') + '/healthz', { cache: 'no-store' })
      .then((r) => r.json())
      .then((h: { signIn?: { st8n?: boolean; name?: string; email?: boolean; local?: boolean; mail?: boolean; setupNeeded?: boolean } }) => {
        const si = h?.signIn;
        if (live && si) setO({ oidc: si.st8n === true && !isPortal(), name: typeof si.name === 'string' ? si.name : null, email: si.email === true && !isPortal(), local: si.local === true, mail: si.mail !== false, setupNeeded: si.setupNeeded === true });
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  return o;
}

/** [LIC D1] Email codes: the address, then the six digits from the email; signed in, the app starts again. */
function EmailCode() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const post = async (path: string, body: object) => {
    const r = await fetch(authBase() + path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error((j as { error?: string }).error || 'That did not work. Try again.');
    return j;
  };
  const go = async () => {
    setBusy(true); setError(null);
    try {
      if (step === 'email') { await post('/email/start', { email }); setStep('code'); }
      else { await post('/email/verify', { email, code }); location.reload(); }
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <form className="mt-6 grid gap-2.5" data-testid="email-sign-in" onSubmit={(e) => { e.preventDefault(); void go(); }}>
      {step === 'email'
        ? <input className="field field-lg" type="email" autoComplete="email" required placeholder="you@example.com" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" />
        : <input className="field field-lg text-[20px] tracking-[0.3em]" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} required placeholder="000000" aria-label="Code from the email" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} data-testid="code-input" />}
      <button className="btn-primary btn-lg" disabled={busy} data-testid="email-go">{busy ? 'One moment…' : step === 'email' ? 'Email me a code' : 'Sign in'}</button>
      {step === 'code' ? <p className="text-[13px] text-dim">We sent a code to {email}. <button type="button" className="underline" onClick={() => { setStep('email'); setCode(''); }}>Use another address</button></p> : null}
      {error ? <p className="text-[14px] text-bad-text" role="alert">{error}</p> : null}
    </form>
  );
}


type Screen = 'welcome' | 'signin' | 'signup' | 'verify' | 'forgot' | 'reset' | 'invite';
/** A portal install (config.front): Sign in at /, an account only through an invite, no st8n, no marketing. */
const isPortal = () => config.front === 'portal';
const company = () => config.brandName || 'Deleg8';
const homeScreen = (): Screen => (isPortal() ? 'signin' : 'welcome');
const startScreen = (): Screen => {
  const p = location.pathname;
  if (p === '/verify-email') return 'verify';
  if (p === '/reset-password') return 'reset';
  if (p === '/signin') return 'signin';
  if (p === '/invite') return 'invite';
  if (p === '/forgot') return 'forgot';
  if (p === '/signup' && !isPortal()) return 'signup';
  const q = new URLSearchParams(location.search);
  return q.get('signin') === 'email' || q.get('auth_error') === LINK_ERROR || q.get('auth_error') === 'st8n_reauth' ? 'signin' : homeScreen();
};
/** st8n sent the person back: an account with their st8n email exists here. They sign in with the password and the server joins the two. */
const LINK_ERROR = 'st8n_email_exists';
const linking = () => new URLSearchParams(location.search).get('auth_error') === LINK_ERROR;
/** The address st8n gave (GET /api/v1/auth/pending-link -> { email, provider }); null when none is waiting. */
function usePendingLink(): string | null {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    if (!linking() || config.mock) return;
    fetch(config.apiBase.replace(/\/$/, '') + '/auth/pending-link', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null)).then((j: { email?: string } | null) => { if (j?.email) setEmail(j.email); }).catch(() => {});
  }, []);
  return email;
}
/** A same-site path only: starts with one "/", never "//" or "/\\", no scheme, no control characters. Else null. */
export function safeNext(v: string | null | undefined): string | null {
  if (!v || v[0] !== '/' || v[1] === '/' || v[1] === '\\' || /[\u0000-\u001f\u007f\\]/.test(v)) return null;
  return v;
}
/** What the page was opened for: st8n_reauth ("sign in again to link st8n") and where to go afterwards. */
const reauthIntent = () => {
  const q = new URLSearchParams(location.search);
  return q.get('auth_error') === 'st8n_reauth' ? { next: safeNext(q.get('next')) } : null;
};
const tokenInUrl = () => new URLSearchParams(location.search).get('token') ?? '';
/** The address a link was sent to: a team invite's `/signin?email=`, or `?e=`. Read once: moving between screens clears the URL. */
const emailInUrl = () => { const q = new URLSearchParams(location.search); return (q.get('email') ?? q.get('e') ?? '').trim(); };

/** POST to /auth/local/<path>: the JSON answer, or an Error carrying the server's sentence and code. */
async function local<T = Record<string, unknown>>(path: string, body: object): Promise<T> {
  let r: Response;
  try {
    r = await fetch(authBase() + '/local/' + path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' });
  } catch {
    throw Object.assign(new Error('Cannot reach Deleg8. Check your connection and try again.'), { code: 'offline' });
  }
  const j = (await r.json().catch(() => ({}))) as { error?: string; code?: string; url?: string };
  if (!r.ok) throw Object.assign(new Error(j.error || 'That did not work. Try again.'), { code: j.code, url: j.url });
  return j as T;
}

const input = 'field field-lg';   // 16px: iOS does not zoom the page on focus
const primary = 'btn-primary btn-lg w-full';
const quiet = 'inline-flex min-h-[44px] items-center px-1 text-[15px] text-accent-text underline-offset-2 hover:underline';

function Err({ text }: { text: string | null }) {
  return text ? <p className="text-[14px] text-bad-text" role="alert" data-testid="auth-error">{text}</p> : null;
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="grid gap-1.5 text-[14px] font-medium text-dim">{label}{children}</label>;
}
function useForm() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  return { busy, error, setError, run };
}
function MailNote({ mail }: { mail: 'sent' | 'console' | null }) {
  return mail === 'console'
    ? <p className="rounded-xl bg-warn-soft px-3.5 py-2.5 text-[13px] text-warn-text" data-testid="auth-mail-console">No email is set up on this server, so nothing was sent. The link is in the server’s log.</p>
    : null;
}

/** Create the account: name, email, password. The next step is the email we send. */
function SignUp({ go, onSent, initialEmail }: { go: (s: Screen) => void; onSent: (email: string, mail: 'sent' | 'console') => void; initialEmail: string }) {
  const [name, setName] = useState(''); const [email, setEmail] = useState(initialEmail); const [password, setPassword] = useState('');
  const f = useForm();
  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); void f.run(async () => { const r = await local<{ mail: 'sent' | 'console' }>('signup', { name, email, password }); onSent(email, r.mail); }); }}>
      <Field label="Name"><input className={input} autoComplete="name" enterKeyHint="next" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-signup-name" /></Field>
      <Field label="Email"><input className={input} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} enterKeyHint="next" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-signup-email" /></Field>
      <Field label="Password"><input className={input} type="password" autoComplete="new-password" enterKeyHint="go" required minLength={10} placeholder="At least 10 characters" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-signup-password" /></Field>
      <Err text={f.error} />
      <button className={primary} disabled={f.busy} data-testid="auth-signup-submit">{f.busy ? 'One moment…' : 'Create account'}</button>
      <p className="text-center text-[15px] text-dim">Already have an account? <button type="button" className={quiet} onClick={() => go('signin')} data-testid="auth-signup-to-signin">Sign in</button></p>
    </form>
  );
}

function CheckEmail({ email, mail, go }: { email: string; mail: 'sent' | 'console'; go: (s: Screen) => void }) {
  const f = useForm();
  const [again, setAgain] = useState<'sent' | 'console' | null>(null);
  return (
    <div className="grid gap-4" data-testid="auth-check-email">
      {isPortal()
        ? <p className="text-[15px] text-dim">If <b className="text-ink">{email}</b> has an invitation to {company()}, we sent it a link. Open it on this device to finish creating your account.</p>
        : <p className="text-[15px] text-dim">We sent a link to <b className="text-ink">{email}</b>. Open it on this device to finish creating your account.</p>}
      <MailNote mail={again ?? mail} />
      {again === 'sent' ? <p className="text-[14px] text-ok-text" role="status">Sent again.</p> : null}
      <Err text={f.error} />
      <button type="button" className="btn-line btn-lg w-full" disabled={f.busy} data-testid="auth-verify-resend"
        onClick={() => void f.run(async () => { const r = await local<{ mail: 'sent' | 'console' }>('resend', { email }); setAgain(r.mail); })}>{f.busy ? 'One moment…' : 'Send the link again'}</button>
      <button type="button" className={quiet} onClick={() => go('welcome')}>Back</button>
    </div>
  );
}

type SignedAnswer = { st8nLink?: { linked: boolean; next?: string; code?: string } };
function SignInForm({ go, onDone, onUnverified, reauth, initialEmail }: { go: (s: Screen) => void; onDone: (next?: string | null) => void; onUnverified: (email: string) => void; reauth: { next: string | null } | null; initialEmail: string }) {
  const [blocked, setBlocked] = useState<string | null>(null);
  const pending = usePendingLink();
  const [email, setEmail] = useState(initialEmail);
  useEffect(() => { if (pending) setEmail(pending); }, [pending]); const [password, setPassword] = useState('');
  const f = useForm();
  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); void f.run(async () => {
      try {
        const r = await local<SignedAnswer>('signin', { email, password });
        if (r.st8nLink && !r.st8nLink.linked) { setBlocked(r.st8nLink.code === 'taken' ? 'That st8n account is already linked to another Deleg8 account.' : 'st8n could not be linked to this account.'); return; }
        onDone(r.st8nLink?.next ?? reauth?.next);
      }
      catch (err) {
        // A customer account belongs on its own address (DELEG8_CUSTOMER_HOSTS): go there.
        const e = err as { code?: string; url?: string };
        if (e.code === 'portal_host' && e.url && /^https?:\/\//.test(e.url)) { location.assign(e.url); return; }
        if ((err as { code?: string }).code === 'unverified') { f.setError(null); onUnverified(email); } else throw err; }
    }); }}>
      {reauth ? <p className="rounded-xl bg-accent-soft px-3.5 py-2.5 text-[14px] text-accent-text" role="status" data-testid="auth-signin-reauth">For your security, sign in again to link st8n.</p> : null}
      {blocked ? <div className="grid gap-2" role="alert" data-testid="auth-signin-link-failed"><p className="text-[14px] text-bad-text">{blocked}</p><button type="button" className="btn-line h-[48px] rounded-2xl" onClick={() => onDone(null)} data-testid="auth-signin-continue">Continue to Deleg8</button></div> : null}
      {linking() ? <p className="rounded-xl bg-accent-soft px-3.5 py-2.5 text-[14px] text-accent-text" role="status" data-testid="auth-link-needed">An account with {pending ?? 'this email'} already exists. Sign in with your password to link st8n.</p> : null}
      <Field label="Email"><input className={input} type="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} enterKeyHint="next" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-signin-email" /></Field>
      <Field label="Password"><input className={input} type="password" autoComplete="current-password" enterKeyHint="go" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-signin-password" /></Field>
      <Err text={f.error} />
      <button className={primary} disabled={f.busy} data-testid="auth-signin-submit">{f.busy ? 'Signing in…' : 'Sign in'}</button>
      <div className="flex flex-wrap items-center justify-between gap-x-4">
        <button type="button" className={quiet} onClick={() => go('forgot')} data-testid="auth-signin-to-forgot">Forgot your password?</button>
        {isPortal() ? null : <button type="button" className={quiet} onClick={() => go('signup')} data-testid="auth-signin-to-signup">Create account</button>}
      </div>
      {isPortal() ? <p className="pt-1 text-center text-[13px] text-faint">Setting this up? <button type="button" className="inline-flex min-h-[44px] items-center px-1 text-accent-text underline underline-offset-2" onClick={() => go('signup')} data-testid="auth-signin-owner-signup">Create the owner account</button></p> : null}
    </form>
  );
}

function Forgot({ go }: { go: (s: Screen) => void }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState<'sent' | 'console' | null>(null);
  const f = useForm();
  if (done) return (
    <div className="grid gap-4" data-testid="auth-forgot-sent">
      <p className="text-[15px] text-dim">If <b className="text-ink">{email}</b> has an account, we sent it a link to choose a new password. It works for an hour.</p>
      <MailNote mail={done} />
      <button type="button" className={primary} onClick={() => go('signin')}>Back to sign in</button>
    </div>
  );
  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); void f.run(async () => { const r = await local<{ mail: 'sent' | 'console' }>('forgot', { email }); setDone(r.mail); }); }}>
      <p className="text-[15px] text-dim">Give your email and we will send a link to choose a new password.</p>
      <Field label="Email"><input className={input} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} enterKeyHint="go" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-forgot-email" /></Field>
      <Err text={f.error} />
      <button className={primary} disabled={f.busy} data-testid="auth-forgot-submit">{f.busy ? 'One moment…' : 'Send the link'}</button>
      <button type="button" className={quiet} onClick={() => go('signin')}>Back to sign in</button>
    </form>
  );
}

function Reset({ go, onDone }: { go: (s: Screen) => void; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const f = useForm();
  const token = tokenInUrl();
  if (!token) return <div className="grid gap-4"><p className="text-[15px] text-dim">This link is not complete. Ask for a new one.</p><button className={primary} onClick={() => go('forgot')}>Send a new link</button></div>;
  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); void f.run(async () => { await local('reset', { token, password }); onDone(); }); }}>
      <Field label="New password"><input className={input} type="password" autoComplete="new-password" enterKeyHint="go" required minLength={10} placeholder="At least 10 characters" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-reset-password" /></Field>
      <Err text={f.error} />
      <button className={primary} disabled={f.busy} data-testid="auth-reset-submit">{f.busy ? 'One moment…' : 'Save and sign in'}</button>
      <button type="button" className={quiet} onClick={() => go('forgot')}>Send a new link</button>
    </form>
  );
}

/** The link in the confirm-your-email message: spend it, and the person is signed in. */
function Verify({ go, onDone }: { go: (s: Screen) => void; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    local('verify', { token: tokenInUrl() }).then(onDone, (e) => setError(errText(e)));
  }, [onDone]);
  if (!error) return <p className="flex items-center gap-3 text-[15px] text-dim" role="status"><span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-accent" />Confirming your email…</p>;
  return (
    <div className="grid gap-4" data-testid="auth-verify-failed">
      <Err text={error} />
      <button className={primary} onClick={() => go('signin')}>Sign in</button>
      <p className="text-[14px] text-dim">Sign in with your password and we will send a new link if your email is still unconfirmed.</p>
    </div>
  );
}

const TITLES: Record<Screen, [string, string]> = {
  welcome: ['Welcome to Deleg8', 'Hand the work out. Get it back done.'],
  signin: ['Sign in', 'Welcome back.'],
  signup: ['Create your account', 'An account of your own.'],
  verify: ['Confirm your email', ''],
  forgot: ['Forgot your password?', ''],
  reset: ['Choose a new password', ''],
  invite: ['', 'Create your account to see what was shared with you.'],
};

/**
 * The signed-out page. A phone sees one column: a title and the form, buttons 52 px tall, fields 16 px so iOS does not
 * zoom, padding for the notch and the home bar. A wide window puts what Deleg8 is beside the form.
 * `onDone` is called once a cookie has been set (email + password): the app loads the person.
 */
export function SignIn({ onSignIn, onDone, offline, expired }: { onSignIn: () => Promise<void>; onDone?: () => void; offline?: boolean; expired?: boolean }) {
  const opts = useSignInOptions();
  const [screen, setScreen] = useState<Screen>(startScreen);
  const [sent, setSent] = useState<{ email: string; mail: 'sent' | 'console' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const provider = opts.name ?? 'st8n';
  const emailOn = opts.local || config.mock;
  const [reauth] = useState(reauthIntent);
  const [linkEmail] = useState(emailInUrl);
  const finish = useCallback((next?: string | null) => {
    const to = safeNext(next);
    // Where st8n (or the link that asked for a fresh sign-in) wants the person: a full load, since it may be a server path.
    if (to && !config.mock) { location.assign(to); return; }
    if (location.pathname !== '/' || location.search) history.replaceState(null, '', '/');
    if (config.mock || !onDone) location.reload(); else onDone();
  }, [onDone]);
  const go = (s: Screen) => { setSent(null); setError(null); setScreen(s); if (s !== 'verify' && s !== 'reset' && location.pathname !== '/') history.replaceState(null, '', '/'); };
  const [title, sub] = !sent && isPortal() && screen === 'signin' ? [`Sign in to ${company()}`, ''] : !sent && isPortal() && screen === 'signup' ? ['Create the owner account', `Only the addresses set as owners for ${company()} can make an account here. Everyone else needs an invitation.`] : !sent && screen === 'invite' ? [`You’ve been invited to ${company()}`, TITLES.invite[1]] : sent ? ['Check your email', ''] : screen === 'welcome' && (offline || expired) ? [offline ? 'Cannot reach Deleg8' : 'Your sign-in ended', offline ? 'Check your connection, then try again.' : 'Sign in again to carry on. Nothing you saved is lost.'] : TITLES[screen];
  const st8nButton = (
    <button type="button" data-testid="auth-welcome-st8n" className="flex h-[52px] w-full items-center justify-center gap-3 rounded-2xl bg-[var(--text)] px-5 text-[16px] font-semibold text-[var(--bg)] hover:opacity-90 disabled:opacity-60" disabled={busy}
      onClick={async () => { setBusy(true); setError(null); try { if (config.mock) await onSignIn(); else location.assign(`${authBase()}/st8n/start?next=${encodeURIComponent('/')}`); } catch (e) { setError(errText(e)); } finally { setBusy(false); } }}>
      {busy ? 'Signing in…' : `Continue with ${provider}`}
    </button>
  );
  return (
    <div className="h-full overflow-y-auto bg-bg" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="auth-screen" data-screen={sent ? 'check-email' : screen}>
      <div className="mx-auto flex min-h-full w-full max-w-[1100px] flex-col px-4 py-5 sm:px-8 sm:py-6">
        <main className={`grid flex-1 content-start gap-8 py-2 sm:py-10 ${isPortal() ? 'sm:content-center' : 'lg:grid-cols-[1fr_420px] lg:content-center lg:gap-16'}`}>
          {isPortal() ? null : <section className="hidden lg:block">
            <h1 className="text-[48px] leading-[1.08] font-bold tracking-tight">Hand the work out.<br /><span className="text-accent">Get it back done.</span></h1>
            <p className="mt-5 max-w-[540px] text-[17px] text-dim">{config.st8nOn ? 'Deleg8 turns a list of what needs doing into work for your st8n sessions, with an assistant keeping the queue moving and checking every result.' : 'Deleg8 turns a list of what needs doing into tickets your team works and your clients can follow.'}</p>
            <ul className="mt-8 grid max-w-[600px] gap-3 sm:grid-cols-2">
              {(config.st8nOn ? POINTS : STANDALONE_POINTS).map(([icon, t, text]) => {
                const I = Ic[icon];
                return (
                  <li key={t} className="flex gap-3 rounded-2xl border border-line bg-panel p-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-text"><I width={18} height={18} /></span>
                    <span className="text-[14px]"><b className="block font-semibold">{t}</b><span className="text-dim">{text}</span></span>
                  </li>
                );
              })}
            </ul>
          </section>}
          <section className="mx-auto w-full max-w-[460px] rounded-3xl border border-line bg-panel p-5 shadow-2 sm:p-8 lg:max-w-none">
            {screen !== 'welcome' && screen !== 'verify' && screen !== 'invite' && !sent && !(isPortal() && screen === 'signin') ? <button type="button" className="-ml-2 mb-2 inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-[15px] font-medium text-accent-text" onClick={() => go(homeScreen())} data-testid="auth-back"><Ic.Back width={18} height={18} /> Back</button> : null}
            {opts.setupNeeded ? <p className="mb-4 rounded-xl bg-warn-soft px-3.5 py-2.5 text-[13.5px] text-warn-text" role="status" data-testid="auth-setup-needed">This server has no owner yet, so nobody can make an account. Whoever runs it: set DELEG8_OWNER_EMAILS to the owner’s email address and restart it.</p> : null}
            <h2 className="text-[24px] font-semibold tracking-tight" data-testid="auth-title">{title}</h2>
            {sub ? <p className="mt-1.5 mb-5 text-[15px] text-dim">{sub}</p> : <div className="mb-5" />}
            {sent ? <CheckEmail email={sent.email} mail={sent.mail} go={go} /> : null}
            {!sent && screen === 'welcome' ? (
              <div className="grid gap-3">
                {opts.oidc ? st8nButton : null}
                {opts.oidc && !config.mock ? <button type="button" className="btn-line btn-lg w-full" onClick={() => location.assign(`${authBase()}/st8n/signup?next=${encodeURIComponent('/')}`)} data-testid="auth-welcome-st8n-signup">Create an account with {provider}</button> : null}
                {emailOn ? <button type="button" className="btn-line btn-lg w-full" onClick={() => go('signin')} data-testid="auth-welcome-email">Continue with email</button> : null}
                {emailOn ? <p className="pt-1 text-center text-[15px] text-dim">New here? <button type="button" className={quiet} onClick={() => go('signup')} data-testid="auth-welcome-signup">Create an account</button></p> : null}
                {opts.email ? <EmailCode /> : null}
                <Err text={error} />
                {config.mock ? <p className="rounded-xl bg-warn-soft px-3.5 py-2.5 text-[13px] text-warn-text">Demo: signs you in as a sample account. Everything stays in this browser.</p> : null}
                {opts.name || !opts.oidc ? null : <p className="pt-2 text-[13px] text-faint">Using st8n already? Turn on Deleg8 in st8n’s More › Experimental to see your boards beside your sessions.</p>}
              </div>
            ) : null}
            {!sent && screen === 'signin' ? (config.mock
              ? <button className={primary} onClick={() => void onSignIn()} data-testid="auth-signin-submit">Sign in (demo)</button>
              : <SignInForm go={go} onDone={finish} reauth={reauth} initialEmail={linkEmail} onUnverified={(email) => setSent({ email, mail: 'sent' })} />) : null}
            {!sent && screen === 'invite' ? (config.mock ? null : <SignUp go={go} initialEmail={linkEmail} onSent={(email, mail) => setSent({ email, mail })} />) : null}
            {!sent && screen === 'signup' ? (config.mock
              ? <button className={primary} onClick={() => void onSignIn()} data-testid="auth-signup-submit">Create account (demo)</button>
              : <SignUp go={go} initialEmail={linkEmail} onSent={(email, mail) => setSent({ email, mail })} />) : null}
            {!sent && screen === 'forgot' ? <Forgot go={go} /> : null}
            {!sent && screen === 'reset' ? <Reset go={go} onDone={() => finish()} /> : null}
            {!sent && screen === 'verify' ? <Verify go={go} onDone={() => finish()} /> : null}
          </section>
        </main>
      </div>
    </div>
  );
}

/** Settings → Change password (only for an account that has a password here). Not placed yet: Settings is d8-mobile's. */
export function ChangePassword({ csrf }: { csrf: () => string | null }) {
  const [current, setCurrent] = useState(''); const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const f = useForm();
  return (
    <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); void f.run(async () => {
      const r = await fetch(authBase() + '/local/password', { method: 'POST', headers: { 'content-type': 'application/json', 'x-deleg8-csrf': csrf() ?? '' }, body: JSON.stringify({ current, password }), credentials: 'include' });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) throw new Error(j.error || 'That did not work. Try again.');
      setDone(true); setCurrent(''); setPassword('');
    }); }}>
      <Field label="Current password"><input className={input} type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="auth-change-current" /></Field>
      <Field label="New password"><input className={input} type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-change-password" /></Field>
      <Err text={f.error} />
      {done ? <p className="text-[14px] text-ok-text" role="status">Password changed. Your other devices were signed out.</p> : null}
      <button className="btn-primary h-[48px] rounded-2xl text-[16px]" disabled={f.busy} data-testid="auth-change-submit">{f.busy ? 'One moment…' : 'Change password'}</button>
    </form>
  );
}
